import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import axios from "axios";
import { BASE_URL } from "../api";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import CommonTable from "../component/CommonTable";

export default function IbIncome() {
    const handleError = useHandleError();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summaryObj, setSummaryObj] = useState({
        total_trades: 0,
        total_commission: 0,
        total_volume: 0
    });

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const fetchIbIncome = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            try {
                // Try GET method first
                res = await axios.get(`${BASE_URL}/account/ib_commission`, {
                    headers: getApiHeaders()
                });
            } catch (err) {
                // If backend requires POST method
                if (err.response?.status === 405) {
                    res = await axios.post(`${BASE_URL}/account/ib_commission`, {}, {
                        headers: getApiHeaders()
                    });
                } else {
                    throw err;
                }
            }

            if (res && res.data) {
                const resData = res.data.data;

                if (resData && typeof resData === "object" && !Array.isArray(resData)) {
                    // Endpoint returned summary metrics object
                    setSummaryObj({
                        total_trades: resData.total_trades ?? 0,
                        total_commission: resData.total_commission ?? 0,
                        total_volume: resData.total_volume ?? 0
                    });

                    const possibleList =
                        resData.trades ||
                        resData.list ||
                        resData.records ||
                        resData.commissions ||
                        resData.history ||
                        resData.data;

                    if (Array.isArray(possibleList)) {
                        setData(possibleList);
                    } else {
                        setData([]);
                    }
                } else if (Array.isArray(resData)) {
                    setData(resData);
                } else if (Array.isArray(res.data)) {
                    setData(res.data);
                } else {
                    setData([]);
                }

                if (res.data.code !== 200 && res.data.status !== "success" && res.data.status !== true) {
                    if (res.data.message) {
                        MessageToster("error", res.data.message);
                    }
                }
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        fetchIbIncome();
    }, [fetchIbIncome]);

    const clearFilters = () => {
        setFromDate("");
        setToDate("");
        setSearchTerm("");
    };

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const rawDate =
                item.ondate ||
                item.date ||
                item.create_date ||
                item.created_at ||
                item.time ||
                item.open_time;

            if (fromDate) {
                const itemDate = new Date(rawDate);
                const from = new Date(fromDate);
                from.setHours(0, 0, 0, 0);
                if (!isNaN(itemDate.getTime()) && itemDate < from) return false;
            }

            if (toDate) {
                const itemDate = new Date(rawDate);
                const to = new Date(toDate);
                to.setHours(23, 59, 59, 999);
                if (!isNaN(itemDate.getTime()) && itemDate > to) return false;
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                return Object.values(item).some(
                    (val) => val != null && String(val).toLowerCase().includes(term)
                );
            }

            return true;
        });
    }, [data, fromDate, toDate, searchTerm]);

    // Calculate live stats from data
    const stats = useMemo(() => {
        const totalTrades = filteredData.length;
        const totalCommission = filteredData.reduce((acc, r) => {
            const val = Number(
                r.commission_amount ?? r.amount ?? r.commission ?? r.level_commission ?? 0
            );
            return acc + (isNaN(val) ? 0 : val);
        }, 0);

        const totalVolume = filteredData.reduce((acc, r) => {
            if (String(r.commission_type).toLowerCase() === "lot" || r.volume != null) {
                const val = Number(r.volume ?? r.base_amount ?? 0);
                return acc + (isNaN(val) ? 0 : val);
            }
            return acc;
        }, 0);

        return {
            totalTrades: totalTrades > 0 ? totalTrades : (summaryObj.total_trades || 0),
            totalCommission: totalCommission > 0 ? totalCommission : (summaryObj.total_commission || 0),
            totalVolume: totalVolume > 0 ? totalVolume : (summaryObj.total_volume || 0)
        };
    }, [filteredData, summaryObj]);

    const handleExport = () => {
        if (!filteredData || filteredData.length === 0) return;
        const exportRows = filteredData.map((row, idx) => ({
            "No.": idx + 1,
            "Date": formatDateDDMMYYYY(
                row.ondate ||
                row.date ||
                row.create_date ||
                row.created_at ||
                row.time
            ),
            "Trade ID": row.trade_id || row.account_number || "-",
            "Level": row.level_number ?? row.level ?? "-",
            "Commission Type": row.commission_type || "-",
            "Base Amount": row.base_amount ?? row.volume ?? "-",
            "Rate Applied": row.rate_applied != null ? `${row.rate_applied}%` : "-",
            "Commission Amount": row.commission_amount ?? row.amount ?? "-",
            "Status": row.status || "Confirm"
        }));
        exportToExcel(exportRows, "IB_Income_Report", "default");
    };

    const columns = [
        {
            header: "Date",
            render: (row) => {
                const raw =
                    row.ondate ||
                    row.date ||
                    row.create_date ||
                    row.created_at ||
                    row.time ||
                    row.open_time;
                return raw ? formatDateDDMMYYYY(raw) : "-";
            }
        },
        {
            header: "Trade ID",
            render: (row) => (
                <span className="fw-semibold font-monospace text-primary">
                    {row.trade_id || row.referral_account || row.account_number || "-"}
                </span>
            )
        },
        {
            header: "Level",
            render: (row) => {
                const lvl = row.level_number ?? row.level;
                if (lvl == null || lvl === "") return "-";
                return (
                    <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                        Level {lvl}
                    </span>
                );
            }
        },
        {
            header: "Type",
            render: (row) => {
                const type = row.commission_type || row.referral_acc_type || "-";
                const isLot = String(type).toLowerCase() === "lot";
                return (
                    <span
                        className={`badge px-2 py-1 ${isLot
                            ? "bg-info bg-opacity-10 text-info"
                            : "bg-warning bg-opacity-10 text-warning"
                            }`}
                    >
                        {type}
                    </span>
                );
            }
        },
        {
            header: "Base Amount",
            render: (row) => {
                if (row.base_amount == null && row.volume == null) return "-";
                const val = row.base_amount ?? row.volume;
                if (String(row.commission_type).toLowerCase() === "lot") {
                    return <span className="fw-semibold">{val} Lot</span>;
                }
                return <span className="fw-semibold">${Number(val).toFixed(2)}</span>;
            }
        },
        {
            header: "Rate",
            render: (row) => (row.rate_applied != null ? `${row.rate_applied}` : "-")
            // render: (row) => (row.rate_applied != null ? `${row.rate_applied}%` : "-")
        },
        {
            header: "Commission",
            render: (row) => {
                const val =
                    row.commission_amount ??
                    row.amount ??
                    row.commission ??
                    row.level_commission;
                if (val == null || val === "") return "-";
                const num = Number(val);
                if (isNaN(num)) return String(val);
                const formatted =
                    num < 0.01
                        ? num.toFixed(4)
                        : (num.toString().split(".")[1]?.length > 2
                            ? num.toFixed(3)
                            : num.toFixed(2));
                return (
                    <span className="fw-bold text-success font-monospace">
                        ${formatted}
                    </span>
                );
            }
        },
        {
            header: "Status",
            render: (row) => {
                const status = row.status || "Confirm";
                const isConfirm = String(status).toLowerCase() === "confirm";
                return (
                    <span
                        className={`badge px-2 py-1 ${isConfirm
                            ? "bg-success bg-opacity-10 text-success"
                            : "bg-secondary bg-opacity-10 text-secondary"
                            }`}
                    >
                        {status}
                    </span>
                );
            }
        }
    ];

    return (
        <div id="layout-wrapper">
            {loading && <Loader />}
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h2 className="fw-700 mb-sm-0">IB Income</h2>
                                </div>
                            </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="row mb-3">
                            <div className="col-xxl-4 col-md-4 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Commission
                                                </p>
                                                <h3 className="mb-0 fw-bold text-success">
                                                    ${Number(stats.totalCommission).toFixed(2)}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-success bg-opacity-10 text-success font-size-22">
                                                    <i className="bx bx-dollar-circle"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-4 col-md-4 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Trades
                                                </p>
                                                <h3 className="mb-0 fw-bold text-primary">
                                                    {stats.totalTrades}
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-primary bg-opacity-10 text-primary font-size-22">
                                                    <i className="bx bx-trending-up"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xxl-4 col-md-4 col-sm-6 mb-3">
                                <div className="card h-100 mb-0 shadow-sm border-0">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <p className="text-muted fw-medium text-uppercase font-size-12 mb-1">
                                                    Total Volume
                                                </p>
                                                <h3 className="mb-0 fw-bold text-info">
                                                    {Number(stats.totalVolume).toFixed(2)}{" "}
                                                    <span className="font-size-13 text-muted fw-normal">
                                                        Lots
                                                    </span>
                                                </h3>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0 ms-2">
                                                <span className="avatar-title rounded-circle bg-info bg-opacity-10 text-info font-size-22">
                                                    <i className="bx bx-bar-chart-alt-2"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Income Records Table */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                                            <div>
                                                <label className="form-label mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fromDate}
                                                    max={toDate || today}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={toDate}
                                                    min={fromDate || undefined}
                                                    max={today}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">Search</label>
                                                <input
                                                    type="search"
                                                    className="form-control form-control-sm"
                                                    placeholder="Search income..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-secondary waves-effect"
                                                    onClick={clearFilters}
                                                    disabled={!fromDate && !toDate && !searchTerm}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary waves-effect"
                                                    onClick={fetchIbIncome}
                                                    disabled={loading}
                                                    title="Refresh Data"
                                                >
                                                    <i className={`bx bx-refresh me-1 ${loading ? "bx-spin" : ""}`}></i>
                                                    Refresh
                                                </button>
                                            </div>
                                            <div className="ms-auto">
                                                <ExportButton
                                                    onClick={handleExport}
                                                    disabled={!filteredData || filteredData.length === 0}
                                                />
                                            </div>
                                        </div>

                                        <CommonTable
                                            columns={columns}
                                            data={filteredData}
                                            loading={loading}
                                            noDataMessage="No commission records found"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}
